/** @jsxImportSource @emotion/react */

import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Button, Typography } from '@mui/material'

import { add, sub } from 'date-fns'
import { FIND_PROMO_CODE_HISTORY_DATA } from 'queries/promoCode'
import { PromocodeHistoryQueryInput } from '__generatedTypes__/globalTypes'
import {
  findPromocodeHistoryData,
  findPromocodeHistoryDataVariables,
} from '__generatedTypes__/findPromocodeHistoryData'
import { PageLoader } from 'components/PageLoader'
import Select from 'components/Select'
import { convertToDollars } from 'utils/priceFormatter'

import DateTimeInput from 'components/DateTimeInput'
import * as classes from './styles'
import { TextStatistics } from './TextStatistics'
import { DATE_OPTIONS, DateOption, DateOptionsEnum } from './consts'
import { ChartStatistics } from './ChartStatistics'
import { formatDate, getDisplayBy, getFilters } from './utils'

export const Analytics = () => {
  const { promoCodeId = '' } = useParams()

  const initialDate = DATE_OPTIONS[1]
  const [selectedDate, setSelectedDate] = useState(initialDate)
  const [_selectedDate, _setSelectedDate] = useState(initialDate)

  const [filters, setFilters] = useState(getFilters(selectedDate))
  const [_filters, _setFilters] = useState(getFilters(selectedDate))

  const query: PromocodeHistoryQueryInput = {
    where: {
      promocode: {
        id: promoCodeId,
      },
      displayBy: getDisplayBy(selectedDate, filters),
    },
    filters: {
      createdAt:
        selectedDate.id === DateOptionsEnum.Lifetime
          ? undefined
          : {
              from: formatDate(filters.from, false),
              to: formatDate(filters.to, true),
            },
    },
  }

  const { data, loading } = useQuery<findPromocodeHistoryData, findPromocodeHistoryDataVariables>(
    FIND_PROMO_CODE_HISTORY_DATA,
    {
      variables: {
        query: query,
        graphQuery: query,
      },
      fetchPolicy: 'network-only',
    }
  )
  const textData = data?.findPromocodeHistory
  const graphData = data?.findPromocodeHistoryGraph

  const handleApply = () => {
    setSelectedDate(_selectedDate)

    if (_selectedDate.id === DateOptionsEnum.CustomDateRange) {
      setFilters(_filters)
    } else {
      setFilters(getFilters(_selectedDate))
    }
  }

  const handlePreviousClick = () => {
    setFilters({
      from: sub(filters.from, { years: 1 }),
      to: sub(filters.to, { years: 1 }),
    })
  }

  const handleNextClick = () => {
    setFilters({
      from: add(filters.from, { years: 1 }),
      to: add(filters.to, { years: 1 }),
    })
  }

  const renderStats = () => {
    if (loading) {
      return <PageLoader />
    }

    if (!textData || !graphData) {
      return <Typography variant="body1">No data available</Typography>
    }

    return (
      <>
        <TextStatistics
          totalUsed={textData.totalUsed}
          totalRevenue={convertToDollars(textData.totalRevenue)}
          selectedDate={selectedDate}
        />

        <ChartStatistics
          data={graphData}
          selectedDate={selectedDate}
          filters={filters}
          onPrevClick={handlePreviousClick}
          onNextClick={handleNextClick}
        />
      </>
    )
  }

  return (
    <div css={classes.wrapper}>
      <Typography variant="title3" css={classes.title}>
        Discount Codes - Analytics
      </Typography>

      <div css={classes.content}>
        <div css={classes.filtersWrapper}>
          <div css={classes.filters}>
            <Select<DateOption>
              value={_selectedDate}
              onChange={(value) => {
                if (!value) return

                _setSelectedDate(value)
              }}
              items={DATE_OPTIONS}
              fullWidth
            />

            {_selectedDate.id === DateOptionsEnum.CustomDateRange && (
              <div css={classes.dateRangeFilters}>
                <DateTimeInput
                  value={_filters.from ?? null}
                  onChange={(value) => {
                    _setFilters({
                      ..._filters,
                      from: value || new Date(),
                    })
                  }}
                  placeholder="Date from"
                  pickerType="date"
                  format="MMMM d, yyyy"
                  shouldDisableDate={(date) => !!_filters.to && date > _filters.to}
                  withConfirmation={false}
                />
                <DateTimeInput
                  value={_filters.to ?? null}
                  onChange={(value) => {
                    _setFilters({
                      ..._filters,
                      to: value || new Date(),
                    })
                  }}
                  placeholder="Date to"
                  pickerType="date"
                  format="MMMM d, yyyy"
                  shouldDisableDate={(date) => !!_filters.from && date < _filters.from}
                  withConfirmation={false}
                />
              </div>
            )}
          </div>

          <Button variant="outlined" color="blue" disabled={loading} size="large" onClick={handleApply}>
            Apply
          </Button>
        </div>

        {renderStats()}
      </div>
    </div>
  )
}
